import {
	MaterialReactTable,
	useMaterialReactTable,
} from "material-react-table";

import "./CustomTable.scss";
import React, { useMemo, useCallback } from "react";

import { Button, Icon } from "abs-ui-components";
import { useState } from "react";
import { Checkbox } from "@mui/material";

const CustomTable = ({ renderTableData, columnsData, customProps }) => {
	const [selectAll, setSelectAll] = useState(false);
	const [customTableSort, setCustomTableSort] = useState([]);
	const handleSelectAll = (event) => {
		setSelectAll(event.target.checked);
	};

	const custTable = useMaterialReactTable({
		columns: columnsData,
		data: renderTableData || [],
		enableStickyHeader: true,
		enablePagination: false,
		enableSorting: customProps?.enableSort,
		enableColumnVirtualization: customProps.enableColumnVirtualization ?? false,
		enableRowVirtualization: customProps.enableRowVirtualization ?? false,
		enableColumnPinning: false,
		enableColumnActions: false,
		enableEditing: false,
		enableRowSelection: false,
		enableTopToolbar: customProps?.enableToptoolbar,
		enableBottomToolbar: false,
		columnResizeMode: undefined,
		enableStickyFooter: false,
		onSortingChange: setCustomTableSort,
		state: {
			isLoading: customProps?.isLoading,
			sorting: customTableSort,
		},
		initialState: {
			columnOrder: customProps?.columnOrder || [],
			columnPinning: customProps?.columnPinning || (() => { }),
		},

		//for future scope
		renderToolbarInternalActions: ({ table }) => <></>,

		renderTopToolbarCustomActions: ({ table }) => (
			<>
				<div className="container-fluid p-1">
					<div className="row p-0 m-0 d-flex justify-content-between align-items-center">
						<div className="text-color">
							<div>{customProps?.header}</div>
							<div className="text-size">
								{customProps?.subHeader}
							</div>
						</div>
						<div>
							{customProps?.showExportButton && (
								<Button
									disabled={customProps?.exportLoading}
									startIcon={<Icon icon="ios_share" />}
									onClick={customProps?.handleExportTable}
									variant="secondary"
									className="ml-2"
								>
									{customProps?.exportLoading
										? "Please wait..."
										: customProps.exportText ||
										"Export Table"}
								</Button>
							)}
						</div>
					</div>
				</div>
			</>
		),

		muiTableHeadCellProps: ({ column }) => ({
			id: `${column.columnDef.accessorKey}`,
			sx: {
				color: "rgb(255 255 255)",
				height: customProps?.height,
				justifyContent: "center",
				opacity: "1",
				"& .MuiButtonBase-root": {
					color: "white !important",
					"& .MuiTableSortLabel-icon": {
						color: "white !important",
					},
				},
			},
		}),

		muiFilterTextFieldProps: {
			sx: {
				"& .MuiInput-root": {
					color: "white !important",
				},
				"& .MuiButtonBase-root": {
					color: "white !important",
				},
				"& .MuiInputBase-root::after": {
					transform: "none",
					borderBottom: "2px solid white",
				},
			},
		},

		muiColumnActionsButtonProps: {
			sx: {
				color: "white",
			},
		},

		muiSelectAllCheckboxProps: {
			title: selectAll ? "Toggle deselect all" : "Toggle select all",
			onClick: (e) => handleSelectAll(e),
			sx: {
				color: "white",
			},
		},

		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",

				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},

				"& .MuiDivider-root": {
					backgroundColor: "white",
				},
			},
		},

		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiTableBodyCellProps: ({ cell }) => {

			const value = cell.getValue();
			const isBoolean = value === true || value === false;

			const isDecimal = !isNaN(parseFloat(value)) && isFinite(value);

			const roundedValue = isDecimal ? parseFloat(value).toFixed(3) : value;
			return {
				sx: {
					overflow: customProps.tableName === "EUETS" ? "visible" : "hidden",
					opacity: "1",
				},
				title: isDecimal ? value.toString() : undefined, // Set original value as tooltip
				children: !customProps.showSwitch?  (isDecimal ? roundedValue : (isBoolean ? 
				<Checkbox
					className="p-0"
					size="small"
					disabled={!value}
					checked={value}
				/> : value)):null
			};
		},


		muiTableContainerProps: {
			sx: {
				maxHeight: customProps?.maxHeight ?? "400px",
				overflowY: "auto",
			},
		},
		muiTableFooterCellProps: {
			sx: {
				opacity: "1",
			},
		},
	});
	return (
		<>
			<div className="bdn-custom-table-container">
				<MaterialReactTable table={custTable} />
			</div>
		</>
	);
};

const CustomTablePlanVerification = ({ columnsData = [], renderTableData = [], customProps = {} }) => {
  const {
    rowKeyAccessor = "_rowKey",
    ...restCustomProps
  } = customProps;

  const getStableRowKey = useCallback((row, index) => {
    const r = row?.original ?? row;
    const candidate =
      r?.[rowKeyAccessor] ??
      r?.documentMstId ??
      r?.documentId ??
      ((r?.documentFileName || r?.fileName) &&
        `${r.documentFileName || r.fileName}-${r.revision ?? r.revisionNumber ?? 0}`) ??
      index;
    return String(candidate);
  }, [rowKeyAccessor]);

  const columns = useMemo(
    () =>
      (columnsData || []).map((c, i) => ({
        id: c.id ?? c.accessorKey ?? `col-${i}`,
        ...c,
      })),
    [columnsData]
  );

  const data = useMemo(() => renderTableData || [], [renderTableData]);

  return (
    <MaterialReactTable
      columns={columns}
      data={data}
      getRowId={(row, index) => getStableRowKey(row, index)}
      muiTableContainerProps={{
        sx: {
          maxHeight: restCustomProps?.maxHeight ?? "400px",
          overflowY: "auto",
        },
      }}
      {...restCustomProps}
    />
  );
};

export default CustomTable;
